import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { RETURN_ADDRESS, CONTACT_INFO, siteImages } from '../data/mock';
import { MapPin, Clock, CheckCircle2, XCircle, Package, ArrowRight, PawPrint, Mail, Phone } from 'lucide-react';

const ReturnsPage = () => {
  useEffect(() => {
    document.title = 'Returns & Refund Policy | Paw Supplies Group';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main>
        {/* Banner */}
        <section className="relative h-64 md:h-80 overflow-hidden">
          <img 
            src={siteImages.returnsBanner}
            alt="Returns Policy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-brown/85 via-brand-brown/60 to-brand-brown/30" />
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 text-brand-cream mb-2">
                <PawPrint className="w-5 h-5" />
                <span className="text-xs font-bold uppercase tracking-[0.25em]">Hassle-Free Returns</span>
              </div>
              <h1 className="text-4xl md:text-5xl text-white font-extrabold mb-3" className-FONT-DISPLAY>
                Returns & Refunds
              </h1>
              <p className="text-brand-cream max-w-lg">
                We want you and your pet to love every order
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Return Address Card */}
          <div className="bg-gradient-to-br from-brand-cream to-brand-orange-light/20 border border-brand-divider rounded-3xl p-8 mb-12">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <MapPin className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-brand-brown mb-2" className-FONT-DISPLAY>Return Address</h2>
                <p className="text-brand-orange-dark font-semibold text-lg leading-relaxed">
                  {RETURN_ADDRESS.street}<br />
                  {RETURN_ADDRESS.city}, {RETURN_ADDRESS.state} {RETURN_ADDRESS.zip}<br />
                  {RETURN_ADDRESS.country}
                </p>
              </div>
            </div>
          </div>

          {/* Policy Overview Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>30-Day Window</h3>
              <p className="text-brand-brown-light text-sm">Return within 30 days of delivery for a full refund</p>
            </div>
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>Original Condition</h3>
              <p className="text-brand-brown-light text-sm">Items must be unused and in original packaging</p>
            </div>
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-brand-orange-light/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-brand-orange-dark" />
              </div>
              <h3 className="font-bold text-brand-brown text-lg mb-2" className-FONT-DISPLAY>Full Refund</h3>
              <p className="text-brand-brown-light text-sm">Refunds processed within 5-7 business days</p>
            </div>
          </div>

          {/* Eligible / Not Eligible */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6">
              <h3 className="font-bold text-brand-brown text-lg mb-4 flex items-center gap-2" className-FONT-DISPLAY>
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                Eligible for Returns
              </h3>
              <ul className="space-y-3">
                {[
                  'Unused items in original packaging',
                  'Items returned within 30 days',
                  'Defective or damaged products',
                  'Wrong items received',
                  'Items with all tags attached'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-brand-brown-light text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-brand-cream border border-brand-divider rounded-2xl p-6">
              <h3 className="font-bold text-brand-brown text-lg mb-4 flex items-center gap-2" className-FONT-DISPLAY>
                <XCircle className="w-5 h-5 text-red-500" />
                Not Eligible for Returns
              </h3>
              <ul className="space-y-3">
                {[
                  'Used or worn items',
                  'Items without original packaging',
                  'Items returned after 30 days',
                  'Items damaged by misuse',
                  'Personal-care items once opened'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-brand-brown-light text-sm">
                    <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* How to Return */}
          <div className="bg-brand-cream border border-brand-divider rounded-2xl p-8 mb-12">
            <h3 className="font-bold text-brand-brown text-xl mb-6" className-FONT-DISPLAY>How to Return an Item</h3>
            <div className="space-y-6">
              {[
                { num: '1', title: 'Contact Us', desc: 'Email us or call to request a return authorization' },
                { num: '2', title: 'Pack Your Item', desc: 'Securely pack the item in its original packaging' },
                { num: '3', title: 'Ship It Back', desc: 'Send the package to our return address above' },
                { num: '4', title: 'Get Your Refund', desc: 'Refund processed within 5-7 business days of receipt' }
              ].map((step, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-brand-orange rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">{step.num}</span>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-brand-brown mb-1">{step.title}</h4>
                    <p className="text-brand-brown-light text-sm">{step.desc}</p>
                  </div>
                  {i < 3 && <ArrowRight className="w-5 h-5 text-brand-brown-light hidden md:block" />}
                </div>
              ))}
            </div>
          </div>

          {/* Contact for Returns */}
          <div className="bg-brand-cream border border-brand-divider rounded-2xl p-8">
            <h3 className="font-bold text-brand-brown text-xl mb-4" className-FONT-DISPLAY>Need Help with a Return?</h3>
            <p className="text-brand-brown-light mb-6">Our customer support team is here to assist you with any questions about returns or refunds.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-brand-orange text-white font-semibold rounded-full hover:bg-brand-orange transition-colors"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_INFO.email}
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-white text-brand-brown font-semibold rounded-full hover:bg-brand-cream transition-colors border border-brand-divider"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ReturnsPage;
